import { motion } from "framer-motion";
import { useContext } from "react";
import { Link } from "react-router-dom";
import { DataContext } from "../../Context/DataContext";
import { Helmet } from "react-helmet";
import { MonitorPlay, Trophy, Zap } from "lucide-react";

const GameGallery = () => {
  const gameData = useContext(DataContext);

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.15, 
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

 const createSlug = (text) => {
    return text
      ?.toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": gameData?.slice(11, 17)?.map((game, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": game?.name,
      "url": `https://www.gamezonehub.in/play/${createSlug(game?.name)}`
    }))
  };


  return (
    <motion.section className="bg-gradient-to-b lg:px-8 from-[#0f172a] to-[#1e293b] sm:py-16 py-9">
       <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4">
        <motion.h2 className="text-center text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold text-white mb-5" initial={{ y: 30, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: "easeOut" }}>
          🎮 Explore{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">
            Free Online Games
          </span>
        </motion.h2>

        <motion.p 
          className="text-center text-gray-400 sm:text-lg text-[16px] mb-12 max-w-3xl mx-auto leading-relaxed" 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
        >
          Dive into our extensive collection of <strong>free online games</strong>. 
          Whether you enjoy high-speed racing, strategic puzzles, or endless arcade fun, GameZone has something for everyone. 
          Experience lag-free gaming directly in your browser with <strong>no installation required</strong>.
        </motion.p>

        <motion.ul className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
          {gameData?.slice(11, 17)?.map((game, index) => {
             const gameSlug = createSlug(game?.name);

             return (
              <motion.li 
                key={index} 
                variants={cardVariants} 
                className="relative group rounded-2xl overflow-hidden shadow-lg cursor-pointer border border-gray-700 list-none"
              >
                <Link to={`/play/${gameSlug}`} state={{ game: game }} aria-label={`Play ${game?.name} online`}>
                  
                  <motion.div 
                    whileHover={{ scale: 1.07, rotate: [0, -1, 1, 0], transition: { duration: 0.4, ease: "easeInOut" } }}
                    className="h-full w-full"
                  >
                    <motion.img 
                      src={game.image} 
                      alt={`Play ${game?.name} - Free online game at Game Zone Hub`}
                      className="w-full h-[230px] object-cover group-hover:opacity-80 transition" 
                      whileHover={{ scale: 1.1 }} 
                      transition={{ duration: 0.6, ease: "easeOut" }}
                      loading="lazy"
                      decoding="async"
                    />
                    
                    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/60">
                      <motion.h3 
                        className="sm:text-xl text-lg font-bold text-white mb-2" 
                        initial={{ y: 10, opacity: 0 }} 
                        whileInView={{ y: 0, opacity: 1 }} 
                        transition={{ duration: 0.5, delay: 0.1 }}
                      >
                        {game?.name}
                      </motion.h3>
                      
                      <motion.div 
                        className="sm:px-4 sm:py-2 py-2 px-3 sm:text-[15px] text-[15px] font-[600] bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300 text-black rounded-xl transition hover:text-[14px]" 
                        whileTap={{ scale: 0.95 }} 
                        whileHover={{ boxShadow: "0 0 15px rgba(59,130,246,0.7)", scale: 1.05 }}
                      >
                        Play Free Online
                      </motion.div>
                    </div>
                  </motion.div>
                </Link>
              </motion.li>
            );
          })}
        </motion.ul>

        <div
  className="mt-20 border-t border-white/10 pt-10"
  data-aos="fade-up"
>
  <h2
    className="text-2xl font-bold text-white text-center mb-8"
    data-aos="fade-up"
    data-aos-delay="100"
  >
    Discover Your Next Favorite Game
  </h2>

  <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">

  <div
    data-aos="fade-up"
    data-aos-delay="200"
    className=" cursor-pointer bg-[#162032] p-6 rounded-xl border border-white/5 
    transition-all duration-300 ease-out
    hover:-translate-y-2 hover:scale-[1.02]
    hover:border-blue-400/40
    hover:shadow-[0_10px_30px_-10px_rgba(59,130,246,0.6)]"
  >
    <MonitorPlay className="text-blue-400 w-8 h-8 mb-4 mx-auto md:mx-0 
      transition-transform duration-300 group-hover:scale-110" />
    <h3 className="text-lg font-semibold text-white mb-2">Browser Optimized</h3>
    <p className="text-gray-400 text-sm">
      Our games are built for performance. Enjoy smooth gameplay when you <strong>play online games</strong> on Chrome, Safari, or Firefox without any lag.
    </p>
  </div>

  <div
    data-aos="fade-up"
    data-aos-delay="350"
    className="group cursor-pointer bg-[#162032] p-6 rounded-xl border border-white/5 
    transition-all duration-300 ease-out
    hover:-translate-y-2 hover:scale-[1.02]
    hover:border-yellow-400/40
    hover:shadow-[0_10px_30px_-10px_rgba(234,179,8,0.6)]"
  >
    <Trophy className="text-yellow-400 w-8 h-8 mb-4 mx-auto md:mx-0 
      transition-transform duration-300 group-hover:rotate-6" />
    <h3 className="text-lg font-semibold text-white mb-2">Daily Challenges</h3>
    <p className="text-gray-400 text-sm">
      Compete in new levels every day. Test your skills in strategy, puzzle, and action genres to top the leaderboards in our <strong>free browser games</strong>.
    </p>
  </div>

  <div
    data-aos="fade-up"
    data-aos-delay="500"
    className="group cursor-pointer bg-[#162032] p-6 rounded-xl border border-white/5 
    transition-all duration-300 ease-out
    hover:-translate-y-2 hover:scale-[1.02]
    hover:border-purple-400/40
    hover:shadow-[0_10px_30px_-10px_rgba(168,85,247,0.6)]"
  >
    <Zap className="text-purple-400 w-8 h-8 mb-4 mx-auto md:mx-0 
      transition-transform duration-300 group-hover:scale-125" />
    <h3 className="text-lg font-semibold text-white mb-2">Instant Access</h3>
    <p className="text-gray-400 text-sm">
        No accounts needed for most games. Just click and <strong>play free online</strong> instantly. We value your time and gaming experience.
    </p>
  </div>

  </div>

</div>

      </div>
    </motion.section>
  );
};

export default GameGallery;
